import { create } from 'zustand';
import { useGpsStore, GpsFix } from './gpsStore';
import {
  pushRow,
  pushMany,
  deleteRowByClientId,
  pullAll,
  type SyncBindings,
} from '../lib/syncCore';
import { useAuthStore } from './authStore';
import { greatCircleNm } from '../lib/geo';
import {
  trackActiveGet,
  trackActiveSet,
  trackActiveClear,
  trackIndexGetAll,
  trackIndexSet,
  trackIndexDelete,
  trackLogsGetPoints,
  trackLogsSetPoints,
  trackLogsDeletePoints,
} from '../lib/idb';

export interface TrackPoint {
  lat: number;
  lon: number;
  alt?: number | null;
  speed?: number | null;
  heading?: number | null;
  t: number;
}

/** Metadata-only representation of a completed track. Stored in the
 *  `track_index` IDB store and in `useTrackStore.getState().logs`.
 *  No `points` array — those live in `track_logs` IDB store and are
 *  loaded lazily via `loadTrackPoints(id)`.
 *
 *  Audit reference: audit-synthesis-2026-05-24.md § CR-1. */
export interface TrackMeta {
  id: string;
  startedAt: number;
  endedAt?: number;
  /** Number of GPS fixes in the completed track. */
  pointCount: number;
  /** Great-circle distance in nautical miles (sum of consecutive fixes). */
  distanceNm: number;
  notes?: string;
}

/** Full track with its points array. Returned by `loadTrackPoints()` and
 *  constructed internally in `stop()` for the IDB write and server push.
 *  Consumer UI should prefer `TrackMeta` for list views and call
 *  `loadTrackPoints(id)` only when the raw fixes are actually needed
 *  (detail page, debrief, GPX export). */
export interface TrackLog extends TrackMeta {
  points: TrackPoint[];
}

// ── localStorage key constants ────────────────────────────────────────────

/** Completed-tracks localStorage key from before 2026-05-24. Migrated
 *  to IDB on first hydrate and removed. See migrateLegacyTracks(). */
const LEGACY_TRACKS_KEY = 'fyrefly:tracks:v1';

/** Active-track sentinel key in localStorage. Stores tiny metadata so
 *  hydrate() can detect an in-progress recording synchronously without
 *  blocking on an IDB read. The sentinel is written every ~5 seconds
 *  during recording and on page-hide/pagehide events. */
const ACTIVE_SENTINEL_KEY = 'fyrefly:track:active:sentinel:v1';

/** Legacy localStorage key from before 2026-05-22. Migrated to IDB on
 *  first hydrate. Kept so we can rescue any in-flight track that crossed
 *  the upgrade boundary. */
const LEGACY_ACTIVE_KEY = 'fyrefly:track:active:v1';

interface ActiveSentinel {
  id: string;
  startedAt: number;
  pointCount: number;
}

// ── Active-sentinel helpers ───────────────────────────────────────────────

function loadActiveSentinel(): ActiveSentinel | null {
  try {
    const raw = localStorage.getItem(ACTIVE_SENTINEL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveSentinel;
    if (!parsed || typeof parsed.id !== 'string' || typeof parsed.startedAt !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeActiveSentinel(t: TrackLog | null) {
  try {
    if (t == null) {
      localStorage.removeItem(ACTIVE_SENTINEL_KEY);
    } else {
      const s: ActiveSentinel = {
        id: t.id,
        startedAt: t.startedAt,
        pointCount: t.points.length,
      };
      localStorage.setItem(ACTIVE_SENTINEL_KEY, JSON.stringify(s));
    }
  } catch { /* quota — sentinel is tiny so this should never happen */ }
}

/** Persist the active track to IDB. Fire-and-forget; callers don't await
 *  to keep the GPS hot path non-blocking. The sentinel is written
 *  synchronously alongside so cold-start can detect the in-progress
 *  recording even if the IDB write was still in-flight at shutdown. */
function saveActive(t: TrackLog | null) {
  writeActiveSentinel(t);
  if (t == null) {
    void trackActiveClear();
  } else {
    void trackActiveSet<TrackLog>(t).catch(() => {
      // IDB quota errors are extremely rare — budget is ~50 MB+. If
      // they happen we fall through; the sentinel still flags an
      // in-progress recording so we don't lose the entire track
      // metadata, just the most recent N points since the last
      // successful flush. Surface via console for now; a future PR
      // can hook this into connectivityStore.degraded for a status
      // pill.
      // eslint-disable-next-line no-console
      console.warn('[trackStore] active-track IDB write failed');
    });
  }
}

// ── One-shot migration helpers ────────────────────────────────────────────

/** Migrate the pre-2026-05-22 localStorage active-track blob (if any)
 *  into IDB and clean up. Returns the migrated TrackLog or null. */
async function migrateLegacyActive(): Promise<TrackLog | null> {
  try {
    const raw = localStorage.getItem(LEGACY_ACTIVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TrackLog;
    if (!parsed || typeof parsed.id !== 'string' || !Array.isArray(parsed.points)) {
      localStorage.removeItem(LEGACY_ACTIVE_KEY);
      return null;
    }
    await trackActiveSet<TrackLog>(parsed);
    writeActiveSentinel(parsed);
    localStorage.removeItem(LEGACY_ACTIVE_KEY);
    // eslint-disable-next-line no-console
    console.info('[trackStore] migrated legacy active track to IDB:', parsed.id, parsed.points.length, 'pts');
    return parsed;
  } catch {
    try { localStorage.removeItem(LEGACY_ACTIVE_KEY); } catch { /* ignore */ }
    return null;
  }
}

/** One-shot migration of pre-2026-05-24 completed tracks stored in the
 *  legacy `fyrefly:tracks:v1` localStorage key. Writes each track's
 *  points to `track_logs` IDB and its metadata to `track_index`, then
 *  removes the localStorage key. Best-effort: per-entry failures are
 *  logged and skipped; the key is removed at the end regardless so this
 *  doesn't retry indefinitely.
 *
 *  Idempotent: if `fyrefly:tracks:v1` is absent this is a no-op. */
async function migrateLegacyTracks(): Promise<void> {
  try {
    const raw = localStorage.getItem(LEGACY_TRACKS_KEY);
    if (!raw) return;

    let entries: TrackLog[] = [];
    try {
      entries = JSON.parse(raw);
      if (!Array.isArray(entries)) entries = [];
    } catch {
      localStorage.removeItem(LEGACY_TRACKS_KEY);
      return;
    }

    let migrated = 0;
    for (const entry of entries) {
      try {
        if (!entry?.id || typeof entry.startedAt !== 'number' || !Array.isArray(entry.points)) {
          continue;
        }
        const stats = quickStats(entry.points);
        const meta: TrackMeta = {
          id: entry.id,
          startedAt: entry.startedAt,
          endedAt: entry.endedAt,
          pointCount: entry.points.length,
          distanceNm: Number.isFinite(stats.dist) ? Number(stats.dist.toFixed(3)) : 0,
          notes: entry.notes,
        };
        await trackLogsSetPoints(entry.id, entry.points);
        await trackIndexSet(entry.id, meta);
        migrated++;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[trackStore] migrateLegacyTracks: failed for entry', entry?.id, e);
      }
    }
    localStorage.removeItem(LEGACY_TRACKS_KEY);
    // eslint-disable-next-line no-console
    console.info(
      `[trackStore] migrated ${migrated}/${entries.length} legacy completed tracks to IDB`,
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[trackStore] migrateLegacyTracks threw:', e);
    try { localStorage.removeItem(LEGACY_TRACKS_KEY); } catch { /* ignore */ }
  }
}

// ── Store interface ───────────────────────────────────────────────────────

interface TrackState {
  recording: boolean;
  current: TrackLog | null;
  points: TrackPoint[]; // points of current track for live render
  /**
   * Monotonically incrementing counter bumped every time `points` gains
   * a new entry. Subscribers that need to detect "new GPS fix arrived"
   * should select this counter instead of relying on `points` array
   * identity. (Claude audit 2026-05-18, finding 5)
   */
  pointsVersion: number;
  /** Metadata-only list of completed tracks. Full points are stored in
   *  `track_logs` IDB and loaded lazily via `loadTrackPoints(id)`.
   *  Sorted newest-first. */
  logs: TrackMeta[];
  unsub: (() => void) | null;
  start: () => void;
  stop: () => void;
  remove: (id: string) => void;
  clearAll: () => void;
  /** Restore in-progress track from IDB/localStorage on app load and,
   *  if one exists, re-attach the GPS subscriber so recording continues
   *  seamlessly. Call once from main.tsx. Also runs one-shot migrations. */
  hydrate: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function pointFromFix(fix: GpsFix): TrackPoint {
  return {
    lat: fix.lat,
    lon: fix.lon,
    alt: fix.altMeters ?? null,
    speed: fix.speedMps ?? null,
    heading: fix.headingDeg ?? null,
    t: fix.timestamp,
  };
}

function quickStats(points: TrackPoint[]): { dist: number; maxAltFt: number | null } {
  let dist = 0;
  let maxAlt: number | null = null;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    dist += greatCircleNm(a.lat, a.lon, b.lat, b.lon);
    if (b.alt != null) {
      const ft = b.alt * 3.28084;
      if (maxAlt == null || ft > maxAlt) maxAlt = ft;
    }
  }
  if (points[0]?.alt != null) {
    const ft = points[0].alt * 3.28084;
    if (maxAlt == null || ft > maxAlt) maxAlt = ft;
  }
  return { dist, maxAltFt: maxAlt };
}

// ── GPS subscriber (shared between start() and hydrate()) ─────────────────

/** Throttle-debounce timer for active-track IDB snapshot. */
let activeWriteTimer: number | null = null;

function attachGpsSubscriber(
  set: (partial: Partial<TrackState>) => void,
  get: () => TrackState,
): () => void {
  return useGpsStore.subscribe((state, prev) => {
    const f = state.fix;
    if (!f) return;
    if (prev.fix && f.timestamp === prev.fix.timestamp) return;
    const pt = pointFromFix(f);
    const cur = get().current;
    if (!cur) return;
    cur.points.push(pt);
    set({ pointsVersion: get().pointsVersion + 1 });
    // Throttle the active-track snapshot to every ~5 seconds so we
    // don't grind IDB. A 5s gap means at most 5 lost points on a
    // sudden tab kill — acceptable trade-off for not churning writes.
    if (activeWriteTimer == null) {
      activeWriteTimer = window.setTimeout(() => {
        activeWriteTimer = null;
        const c = get().current;
        if (c) saveActive(c);
      }, 5000);
    }
  });
}

// ── Store ─────────────────────────────────────────────────────────────────

export const useTrackStore = create<TrackState>((set, get) => ({
  pointsVersion: 0,
  recording: false,
  current: null,
  points: [],
  // Start empty; hydrate() populates from track_index IDB asynchronously.
  logs: [],
  unsub: null,

  start: () => {
    if (get().recording) return;
    useGpsStore.getState().start();
    const id = `t-${Date.now()}`;
    const startedAt = Date.now();
    const initialFix = useGpsStore.getState().fix;
    const seedPoints = initialFix ? [pointFromFix(initialFix)] : [];
    const current: TrackLog = { id, startedAt, points: seedPoints, pointCount: 0, distanceNm: 0 };
    const unsub = attachGpsSubscriber(set, get);
    set({ recording: true, current, points: seedPoints, pointsVersion: get().pointsVersion + 1, unsub });
    saveActive(current);
  },

  stop: () => {
    const { current, unsub } = get();
    if (unsub) unsub();
    if (!current) {
      set({ recording: false, unsub: null });
      // Nothing to finalize — clear any stale sentinel.
      void trackActiveClear();
      writeActiveSentinel(null);
      return;
    }
    current.endedAt = Date.now();

    // Compute metadata before the async path so we have a stable snapshot.
    const stats = quickStats(current.points);
    const meta: TrackMeta = {
      id: current.id,
      startedAt: current.startedAt,
      endedAt: current.endedAt,
      pointCount: current.points.length,
      distanceNm: Number.isFinite(stats.dist) ? Number(stats.dist.toFixed(3)) : 0,
      notes: current.notes,
    };

    // Immediately update UI: stop the live recording indicator and
    // clear the current track reference. GPS is already unsubscribed.
    // We do NOT update `logs` yet — we wait until the IDB write
    // confirms durability before adding the metadata to the list.
    set({
      recording: false,
      current: null,
      points: [],
      pointsVersion: get().pointsVersion + 1,
      unsub: null,
    });

    // Capture points before any possible GC / mutation.
    const capturedPoints = current.points.slice();
    const capturedId = current.id;

    // Durable IDB finalization — MUST complete before clearing track_active.
    // If this throws, track_active and its sentinel are preserved so the
    // user can recover the recording on next launch (hydrate will detect
    // the sentinel and restore recording mode).
    void (async () => {
      try {
        await trackLogsSetPoints(capturedId, capturedPoints);
        await trackIndexSet(capturedId, meta);
        // Both writes confirmed — safe to clear the active sentinel.
        writeActiveSentinel(null);
        void trackActiveClear();
        // Update the logs list with the new metadata entry.
        set({ logs: [meta, ...get().logs] });
        // Push to server with full points. Doing it AFTER the IDB write
        // keeps local-first semantics: offline pilots don't lose data.
        void pushRow({ ...meta, points: capturedPoints }, TRACK_LOGS_SYNC);
      } catch (err) {
        // IDB finalization failed. track_active is preserved intentionally
        // so the pilot can recover the recording on next app launch.
        // This is the only safe failure mode — losing the track silently
        // is not acceptable. (audit-synthesis-2026-05-24 § CR-1)
        // eslint-disable-next-line no-console
        console.warn(
          '[trackStore] stop(): IDB finalization failed — track_active preserved for recovery on next launch.',
          err,
        );
        // TODO: surface a user-visible toast via connectivityStore.degraded
        // in a follow-up PR (HI-5' adds the degraded-auth banner pattern).
      }
    })();
  },

  remove: (id) => {
    const next = get().logs.filter((l) => l.id !== id);
    set({ logs: next });
    // Best-effort IDB cleanup — fire-and-forget.
    void trackIndexDelete(id);
    void trackLogsDeletePoints(id);
    void deleteRowByClientId(id, TRACK_LOGS_SYNC);
  },

  clearAll: () => {
    const ids = get().logs.map((l) => l.id);
    set({ logs: [] });
    for (const id of ids) {
      void trackIndexDelete(id);
      void trackLogsDeletePoints(id);
      void deleteRowByClientId(id, TRACK_LOGS_SYNC);
    }
  },

  hydrate: () => {
    // Kick off async hydration from IDB. The store is usable immediately
    // (with empty `logs`) and updates once IDB reads complete.
    void (async () => {
      // 1. One-shot migration of the legacy fyrefly:tracks:v1 localStorage
      //    key. Idempotent; no-op if already migrated (key absent).
      await migrateLegacyTracks();

      // 2. Load metadata index — this is fast even with 100+ tracks.
      const rawMetas = await trackIndexGetAll<TrackMeta>();
      // Sort newest-first so the list view doesn't need to sort.
      rawMetas.sort((a, b) => (b.startedAt ?? 0) - (a.startedAt ?? 0));
      set({ logs: rawMetas });

      // 3. Active-track hydration (same as before but inlined here so
      //    we run after the metadata load, not before).
      const sentinel = loadActiveSentinel();
      if (!sentinel) {
        // No sentinel — but we might have a legacy localStorage blob.
        const legacy = await migrateLegacyActive();
        if (legacy && Array.isArray(legacy.points)) {
          useGpsStore.getState().start();
          const unsub = attachGpsSubscriber(set, get);
          set({
            recording: true,
            current: legacy,
            points: legacy.points,
            pointsVersion: get().pointsVersion + 1,
            unsub,
          });
        }
        return;
      }

      // Sentinel present — read the in-progress track from IDB.
      let active = await trackActiveGet<TrackLog>();
      if (!active) active = await migrateLegacyActive();
      if (!active || !Array.isArray(active.points)) {
        // Stale sentinel (IDB cleared externally or migration ran). Clean up.
        writeActiveSentinel(null);
        return;
      }
      useGpsStore.getState().start();
      const unsub = attachGpsSubscriber(set, get);
      set({
        recording: true,
        current: active,
        points: active.points,
        pointsVersion: get().pointsVersion + 1,
        unsub,
      });
    })();
  },
}));

// ── Standalone lazy-loader (exported for consumers) ───────────────────────

/** Load the raw GPS points for a completed track from IDB.
 *  Returns an empty array when the track id is unknown or IDB is
 *  unavailable (e.g., tests that haven't run migration yet). */
export async function loadTrackPoints(trackId: string): Promise<TrackPoint[]> {
  const pts = await trackLogsGetPoints<TrackPoint[]>(trackId);
  return pts ?? [];
}

// ── Sync bindings ─────────────────────────────────────────────────────────

interface TrackLogDbRow {
  owner_id: string;
  client_id: string;
  started_at: string;
  ended_at: string | null;
  point_count: number;
  distance_nm: number | null;
  max_alt_ft: number | null;
  notes: string | null;
  points: TrackPoint[];
}

const TRACK_LOGS_SYNC: SyncBindings<TrackLog, TrackLogDbRow> = {
  tableName: 'track_logs',
  upsertConflict: 'owner_id,client_id',
  localIdField: 'id',
  toDbRow(t, ownerId) {
    const stats = quickStats(t.points);
    return {
      owner_id: ownerId,
      client_id: t.id,
      started_at: new Date(t.startedAt).toISOString(),
      ended_at: t.endedAt ? new Date(t.endedAt).toISOString() : null,
      point_count: t.points.length,
      distance_nm: Number.isFinite(stats.dist) ? Number(stats.dist.toFixed(3)) : null,
      max_alt_ft: stats.maxAltFt,
      notes: t.notes ?? null,
      points: t.points,
    };
  },
  fromDbRow(row) {
    return {
      id: row.client_id,
      startedAt: Date.parse(row.started_at),
      endedAt: row.ended_at ? Date.parse(row.ended_at) : undefined,
      points: Array.isArray(row.points) ? row.points as TrackPoint[] : [],
      pointCount: row.point_count ?? 0,
      distanceNm: row.distance_nm ?? 0,
      notes: row.notes ?? undefined,
    };
  },
};

/** Pull authoritative tracks from server. Pushes any local-only
 *  rows first so a fresh sign-in doesn't lose data. */
export async function syncTracksFromServer(): Promise<void> {
  try {
    // Push first — load points from IDB for each local metadata entry
    // so the full TrackLog reaches the server.
    const local = useTrackStore.getState().logs;
    if (local.length > 0) {
      const fullLogs: TrackLog[] = await Promise.all(
        local.map(async (meta) => {
          const pts = await loadTrackPoints(meta.id);
          return { ...meta, points: pts };
        }),
      );
      const pushResult = await pushMany(fullLogs, TRACK_LOGS_SYNC);
      if (!pushResult.ok) {
        // eslint-disable-next-line no-console
        console.warn('[trackStore] syncFromServer push failed:', pushResult.error);
        return;
      }
    }

    // Pull full rows from server (fromDbRow returns TrackLog with points).
    const r = await pullAll(TRACK_LOGS_SYNC);
    if (r.ok) {
      // Store points in IDB and metadata in track_index; keep store lean.
      const metas: TrackMeta[] = [];
      for (const fullLog of r.rows) {
        const stats = quickStats(fullLog.points);
        const meta: TrackMeta = {
          id: fullLog.id,
          startedAt: fullLog.startedAt,
          endedAt: fullLog.endedAt,
          pointCount: fullLog.points.length,
          distanceNm: Number.isFinite(stats.dist) ? Number(stats.dist.toFixed(3)) : 0,
          notes: fullLog.notes,
        };
        await trackLogsSetPoints(fullLog.id, fullLog.points);
        await trackIndexSet(fullLog.id, meta);
        metas.push(meta);
      }
      metas.sort((a, b) => (b.startedAt ?? 0) - (a.startedAt ?? 0));
      useTrackStore.setState({ logs: metas });
    } else {
      // eslint-disable-next-line no-console
      console.warn('[trackStore] syncFromServer pull failed:', r.error);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[trackStore] syncFromServer threw:', e);
  }
}

// Auth-state-aware sync.
useAuthStore.subscribe((state, prev) => {
  if (!prev.user && state.user) {
    void syncTracksFromServer();
  }
});

// Best-effort: flush the active-track snapshot when the page is
// about to be hidden or unloaded. Catches the case where iOS Safari
// is about to evict the backgrounded tab before our 5-sec throttle
// has fired. visibilitychange + pagehide are the iOS-friendly pair.
if (typeof document !== 'undefined') {
  const flush = () => {
    const cur = useTrackStore.getState().current;
    if (cur && useTrackStore.getState().recording) saveActive(cur);
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
  // beforeunload is fired for explicit reloads on most browsers.
  window.addEventListener('beforeunload', flush);
}
